import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"
import { rails_direct_uploads_path } from "../routes"
import { I18n } from 'i18n-js'
import locales from '../locales.json'

// Connects to data-controller="quote-submission"
export default class extends Controller {
  static targets = ["input", "submitButton", "fileList", "fileListItems"]
  static values = {
    token: String
  }

  connect() {
    this.i18n = new I18n(locales)
    this.i18n.locale = document.querySelector("meta[name='locale']")?.content || 'hu'
    this.selectedFiles = [] // Store selected files (File objects)
    this.uploadedFiles = [] // Store uploaded files (with signed_ids)
  }

  handleFiles(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const files = Array.from(this.inputTarget.files)
    
    if (files.length === 0) {
      this.selectedFiles = []
      this.updateFileList()
      this.updateSubmitButton()
      return
    }
    
    // Store selected files (but don't upload yet)
    this.selectedFiles = files
    this.updateFileList()
    this.updateSubmitButton()
    
    // Clear the input field so same files can be selected again
    this.inputTarget.value = ''
  }

  async handleSubmit(event) {
    event.preventDefault()
    event.stopPropagation()
    
    if (this.selectedFiles.length === 0) {
      this.showError('quote_submission.no_files_error')
      return
    }
    
    // Disable submit button during upload and submission
    this.submitButtonTarget.disabled = true
    const originalText = this.submitButtonTarget.textContent
    this.submitButtonTarget.textContent = this.i18n.t('quote_submission.uploading')
    
    // Upload files using DirectUpload
    this.uploadedFiles = []
    let hasError = false
    
    for (const file of this.selectedFiles) {
      try {
        // Upload using DirectUpload
        const blob = await this.uploadWithDirectUpload(file)
        
        this.uploadedFiles.push({
          signed_id: blob.signed_id,
          filename: blob.filename,
          content_type: blob.content_type
        })
      } catch (error) {
        console.error(`Failed to upload file: ${file.name}`, error)
        hasError = true
        this.showError('quote_submission.upload_error', { filename: file.name })
        this.submitButtonTarget.disabled = false
        this.submitButtonTarget.textContent = originalText
        return
      }
    }
    
    if (this.uploadedFiles.length > 0 && !hasError) {
      // Submit the form with signed_ids
      await this.submitForm(this.uploadedFiles)
    }
  }

  updateFileList() {
    if (!this.hasFileListTarget || !this.hasFileListItemsTarget) return
    
    if (this.selectedFiles.length === 0) {
      this.fileListTarget.style.display = 'none'
      this.fileListItemsTarget.innerHTML = ''
      return
    }
    
    // Show file list
    this.fileListTarget.style.display = 'block'
    this.fileListItemsTarget.innerHTML = ''
    
    this.selectedFiles.forEach((file, index) => {
      const li = document.createElement('li')
      li.className = 'list-group-item d-flex justify-content-between align-items-center'
      li.innerHTML = `
        <span>${file.name}</span>
        <small class="text-muted">${this.formatFileSize(file.size)}</small>
      `
      this.fileListItemsTarget.appendChild(li)
    })
  }

  updateSubmitButton() {
    if (this.selectedFiles.length > 0) {
      this.submitButtonTarget.disabled = false
    } else {
      this.submitButtonTarget.disabled = true
    }
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  async uploadWithDirectUpload(file) {
    return new Promise((resolve, reject) => {
      const upload = new DirectUpload(
        file,
        rails_direct_uploads_path(),
        this
      )

      upload.create((error, blob) => {
        if (error) {
          reject(error)
        } else {
          resolve(blob)
        }
      })
    })
  }

  async submitForm(uploadedFiles) {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]').content
      
      // Create form data with signed_ids
      const formData = new FormData()
      uploadedFiles.forEach(file => {
        formData.append('files[]', file.signed_id)
      })
      
      const response = await fetch(this.element.action, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'text/html'
        },
        body: formData
      })
      
      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      // Redirect to the response page (submit.html.erb)
      const html = await response.text()
      document.open()
      document.write(html)
      document.close()
      
    } catch (error) {
      console.error('Error submitting form:', error)
      this.showError('quote_submission.submission_error', { error: error.message })
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.textContent = this.i18n.t('quote_submission.submit_button')
    }
  }

  showError(i18nKey, params = {}) {
    // Get error message using I18n
    const message = this.i18n.t(i18nKey, params) || i18nKey
    
    // Find or create error alert
    let errorAlert = this.element.closest('.card-body').querySelector('.alert-danger')
    if (!errorAlert) {
      errorAlert = document.createElement('div')
      errorAlert.className = 'alert alert-danger alert-dismissible fade show'
      errorAlert.setAttribute('role', 'alert')
      errorAlert.innerHTML = `
        <i class="bi bi-exclamation-triangle-fill me-2"></i>
        <span class="error-message"></span>
        <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
      `
      const cardBody = this.element.closest('.card-body')
      const existingAlert = cardBody.querySelector('.alert-info')
      if (existingAlert) {
        existingAlert.insertAdjacentElement('afterend', errorAlert)
      } else {
        cardBody.insertBefore(errorAlert, cardBody.firstChild)
      }
    }
    
    const messageSpan = errorAlert.querySelector('.error-message')
    if (messageSpan) {
      messageSpan.textContent = message
    }
  }
}

